import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ArrowUturnLeftSvg from '../other/svg/ArrowUturnLeftSvg'
import ClipboardDocumentListSvg from '../other/svg/ClipboardDocumentListSvg'
import CurrencyDollarSvg from '../other/svg/CurrencyDollarSvg'
import MoreHorizontalSvg from '../other/svg/MoreHorizontalSvg'
import PencilSvg from '../other/svg/PencilSvg'
import TrashCanSvg from '../other/svg/TrashCanSvg'

interface VehicleTableRowProps {
  title: string
  year: string
  mileage: string
  vin: string
  status?: 'in_stock' | 'sold'
  isMenuOpen: boolean
  onToggleMenu: () => void
  onCloseMenu: () => void
  onMarkAsSold: () => void
  onMarkAsInStock: () => void
  onEdit: () => void
  onDelete: () => void
  onOpenDetails: () => void
}

interface VehicleMenuItemProps {
  icon: JSX.Element
  label: string
  onClick: () => void
}

const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const VehicleMenuItem = ({ icon, label, onClick }: VehicleMenuItemProps): JSX.Element => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="self-stretch px-2 py-1 bg-backgrounds-bg-field-component rounded-sm inline-flex justify-start items-center gap-2 hover:bg-lightGrey">
      <div className="w-4 h-4 relative overflow-hidden text-buttons-text-secondary flex justify-center items-center">
        {icon}
      </div>
      <div className="flex-1 justify-start text-left text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-5">
        {label}
      </div>
    </button>
  )
}

const VehicleTableRow = ({
  title,
  year,
  mileage,
  vin,
  status,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
  onMarkAsSold,
  onMarkAsInStock,
  onEdit,
  onDelete,
  onOpenDetails
}: VehicleTableRowProps): JSX.Element => {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      if (menuRef.current !== null && !menuRef.current.contains(event.target as Node)) {
        onCloseMenu()
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [isMenuOpen, onCloseMenu])

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpenDetails}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpenDetails()
        }
      }}
      className="self-stretch px-6 py-3 bg-backgrounds-bg-field-component flex justify-center items-center gap-3 cursor-pointer hover:bg-backgrounds-bg-field-component-hover">
      <div className="flex-1 self-stretch flex justify-start items-center gap-3">
        <div className="flex-1 inline-flex flex-col justify-center items-start overflow-hidden">
          <div className="justify-start text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5 truncate w-full">
            {title}
          </div>
          <div className="inline-flex justify-start items-center gap-2 min-w-0">
            <div className="justify-start text-foregrounds-fg-muted text-xs font-medium font-Montserrat leading-4 whitespace-nowrap">
              {mileage}
            </div>
            <div className="justify-start text-foregrounds-fg-muted text-xs font-medium font-Montserrat leading-4 whitespace-nowrap">
              {year}
            </div>
            <div className="justify-start text-foregrounds-fg-muted text-xs font-medium font-Montserrat leading-4 truncate">
              {vin}
            </div>
          </div>
        </div>
        <div
          className="self-stretch px-px flex justify-end items-center overflow-visible"
          onClick={(event) => {
            event.stopPropagation()
          }}>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={onToggleMenu}
              className="p-1.5 bg-transparent rounded-md flex justify-center items-center overflow-hidden hover:bg-lightGrey">
              <MoreHorizontalSvg className="w-3.5 h-3.5 text-foregrounds-fg-muted" />
            </button>
            {isMenuOpen && (
              <div
                className="w-64 p-1 absolute right-0 top-full z-20 bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-start overflow-hidden"
                style={{ boxShadow: menuShadow }}>
                <div className="self-stretch flex flex-col justify-start items-start">
                  <VehicleMenuItem
                    icon={<ClipboardDocumentListSvg className="w-4 h-4" />}
                    label={t('my_vehicles.details')}
                    onClick={() => {
                      onCloseMenu()
                      onOpenDetails()
                    }}
                  />
                  {status === 'sold' ? (
                    <VehicleMenuItem
                      icon={<ArrowUturnLeftSvg className="w-4 h-4" />}
                      label={t('my_vehicles.mark_as_in_stock')}
                      onClick={() => {
                        onCloseMenu()
                        onMarkAsInStock()
                      }}
                    />
                  ) : (
                    <VehicleMenuItem
                      icon={<CurrencyDollarSvg className="w-4 h-4" />}
                      label={t('my_vehicles.mark_as_sold')}
                      onClick={() => {
                        onCloseMenu()
                        onMarkAsSold()
                      }}
                    />
                  )}
                  <div className="self-stretch h-2 relative -mx-1">
                    <div className="absolute left-0 right-0 top-[3px] h-px bg-borders-border-menu-top" />
                    <div className="absolute left-0 right-0 top-[4px] h-px bg-borders-border-menu-bot" />
                  </div>
                  <VehicleMenuItem
                    icon={<PencilSvg className="w-4 h-4" />}
                    label={t('my_vehicles.edit')}
                    onClick={() => {
                      onCloseMenu()
                      onEdit()
                    }}
                  />
                  <VehicleMenuItem
                    icon={<TrashCanSvg className="w-4 h-4" />}
                    label={t('my_vehicles.delete')}
                    onClick={() => {
                      onCloseMenu()
                      onDelete()
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default VehicleTableRow
